'use client';
import { useEffect, useRef } from 'react';
import { Phone } from 'lucide-react';
import { useOperationFailure, useToast } from '../../components/ui/Toast';
import { holdsAnswer } from '../../lib/saveFeedback';
import { Modal, ModalBody, ModalFooter } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { useTranslation } from '../../lib/i18n';
import type { VoiceRingPayload, VoiceRingEndReason } from '../../lib/types';
import { useRingtone } from './ringtone';

export interface IncomingRing {
  ringId: string; ring?: VoiceRingPayload;
  status: 'loading' | 'ready' | 'stale' | 'error' | 'declining';
  reason?: VoiceRingEndReason;
  declineFailure?: { error: unknown };
}
export function IncomingCallPrompt({ incoming, onAccept, onDecline, onRetry, onVerify, onClose, available }: {
  incoming: IncomingRing; onAccept(): void; onDecline(): void; onRetry(): void; onVerify(): Promise<void>; onClose(): void; available: boolean;
}) {
  const { t } = useTranslation();
  const ready = incoming.status === 'ready';
  const held = incoming.declineFailure !== undefined && holdsAnswer(incoming.declineFailure.error);
  useRingtone(ready && available);
  return <Modal title={incoming.ring?.kind === 'done' ? t.voice.ring.doneTitle : t.voice.ring.title} icon={Phone} presentation="center" size="sm"
    data-testid="voice-ring-prompt" onClose={onClose} closeDisabled={incoming.status === 'declining'}>
    <RingFailure failure={incoming.declineFailure} reload={onVerify} />
    <ModalBody gap={4}>
      {incoming.ring && <p className="break-words text-meta text-muted-foreground">{incoming.ring.title}</p>}
      {incoming.status !== 'ready' && <p role="status" className="text-meta text-muted-foreground">{
        incoming.reason ? t.voice.ring[incoming.reason]
          : incoming.status === 'error' ? t.voice.ring.error
          : incoming.status === 'stale' ? t.voice.ring.stale : t.voice.ring.loading
      }</p>}
      {ready && !available && <p role="status" className="text-meta text-muted-foreground">{t.voice.ring.acceptUnavailable}</p>}
    </ModalBody>
    {incoming.status !== 'loading' && incoming.status !== 'declining' && <ModalFooter>
      {ready ? <>
        <Button type="button" variant="ghost" onClick={onDecline} disabled={held}>{t.voice.ring.decline}</Button>
        <Button type="button" variant="accent" onClick={onAccept} disabled={!available || held}>{t.voice.ring.accept}</Button>
      </> : <>
        {incoming.status === 'error' && <Button type="button" variant="outline" onClick={onRetry}>{t.common.retry}</Button>}
        <Button type="button" variant="accent" onClick={onClose}>{t.voice.ring.close}</Button></>}
    </ModalFooter>}
  </Modal>;
}

function RingFailure({ failure, reload }: { failure: IncomingRing['declineFailure']; reload: () => Promise<void> }) {
  const report = useOperationFailure();
  const latestReload = useRef(reload);
  latestReload.current = reload;
  const { dismissKey } = useToast();
  const { t } = useTranslation();
  useEffect(() => {
    if (failure) report('voice-ring-decline', failure.error, { message: t.voice.ring.declineError, reload: () => latestReload.current() });
    else dismissKey('voice-ring-decline');
  }, [failure, report, dismissKey, t.voice.ring.declineError]);
  return null;
}
